import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StaffPageShell from '../components/StaffPageShell';
import CompletenessStatsStrip from '../components/StudentCompleteness/CompletenessStatsStrip';
import MissingFieldFilters from '../components/StudentCompleteness/MissingFieldFilters';
import CompletenessStudentTable from '../components/StudentCompleteness/CompletenessStudentTable';
import useStudentProfileCompleteness, {
  COMPLETENESS_FLAG,
} from '../hooks/useStudentProfileCompleteness';
import usePermissions from '../hooks/usePermissions';
import { ThemeButton } from '../components/ThemeWrapper';

export default function AdminStudentProfileCompletenessPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { hasPermission, role } = usePermissions();
  const canView = role === 'admin' || hasPermission('students.filter');

  const {
    enabled,
    stats,
    items,
    total,
    page,
    totalPages,
    status,
    missingField,
    loading,
    statsLoading,
    error,
    setPage,
    selectMissingField,
    clearMissingField,
    selectStatus,
    refresh,
  } = useStudentProfileCompleteness();

  useEffect(() => {
    if (user && !canView) {
      navigate('/dashboard');
    }
  }, [user, canView, navigate]);

  const statusButtons = [
    { key: 'incomplete', label: 'Any incomplete' },
    { key: 'complete', label: 'Fully complete' },
    { key: 'all', label: 'All active' },
  ];

  if (enabled === false) {
    return (
      <StaffPageShell title="Student profile completeness" wide>
        <p className="theme-text-secondary">
          This feature is turned off. Enable <code>{COMPLETENESS_FLAG}</code> under{' '}
          Feature flags, then return here to review missing profile details and complete
          profiles.
        </p>
      </StaffPageShell>
    );
  }

  if (enabled === null) {
    return (
      <StaffPageShell title="Student profile completeness" wide>
        <p className="theme-text-secondary text-sm">Checking feature flag…</p>
      </StaffPageShell>
    );
  }

  return (
    <StaffPageShell
      title="Student profile completeness"
      subtitle="Find students with missing profile details and track how many profiles are complete"
      wide
      actions={
        <ThemeButton type="button" onClick={refresh} disabled={loading || statsLoading}>
          Refresh
        </ThemeButton>
      }
    >
      {error ? (
        <div className="mb-4 p-3 rounded theme-error-bg theme-error-text text-sm">{error}</div>
      ) : null}

      <CompletenessStatsStrip stats={stats} loading={statsLoading} />

      <div className="flex flex-wrap gap-2 mb-4">
        {statusButtons.map((btn) => {
          const active = !missingField && status === btn.key;
          return (
            <button
              key={btn.key}
              type="button"
              onClick={() => selectStatus(btn.key)}
              className="px-3 py-1.5 rounded-md text-sm border"
              style={{
                borderColor: active
                  ? 'var(--color-primary, #2563eb)'
                  : 'var(--color-border)',
                backgroundColor: active
                  ? 'var(--color-surface-secondary)'
                  : 'var(--color-surface)',
                color: 'var(--color-text)',
                fontWeight: active ? 600 : 400,
              }}
            >
              {btn.label}
            </button>
          );
        })}
      </div>

      <MissingFieldFilters
        byField={stats?.byField || []}
        byCategory={stats?.byCategory || []}
        selectedKey={missingField}
        onSelect={selectMissingField}
        onClear={clearMissingField}
      />

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <p className="text-sm theme-text-secondary">
          Showing {items.length} of {total} student{total === 1 ? '' : 's'}
          {missingField
            ? ` missing “${
                stats?.byField?.find((f) => f.key === missingField)?.label || missingField
              }”`
            : status === 'incomplete'
              ? ' with incomplete profiles'
              : status === 'complete'
                ? ' with complete profiles'
                : ''}
          .
        </p>
        <div className="flex items-center gap-2 text-sm">
          <button
            type="button"
            className="underline theme-text-secondary disabled:opacity-40"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="theme-text">
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            className="underline theme-text-secondary disabled:opacity-40"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <CompletenessStudentTable items={items} loading={loading} />
    </StaffPageShell>
  );
}
